/**
 * WordPress App Store - State management kết nối WordPress API
 */
import { create } from 'zustand';
import type { WPPost, WPUser } from '../services/apiConfig';
import { wpApi } from '../services/apiConfig';

interface AppState {
  // User state
  currentUser: WPUser | null;
  isAuthenticated: boolean;
  
  // Feed state
  posts: WPPost[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  hasMore: boolean;
  page: number;
  
  // Active filters
  activeHashtag: string | null;
  searchQuery: string;
  
  // Actions
  setCurrentUser: (user: WPUser | null) => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loadFeed: () => Promise<void>;
  loadMore: () => Promise<void>;
  likePost: (postId: number) => Promise<void>;
  unlikePost: (postId: number) => Promise<void>;
  refreshPost: (postId: number) => Promise<void>;
  deletePost: (postId: number) => Promise<void>;
  reportPost: (postId: number, reason?: string) => Promise<void>;
  setActiveHashtag: (hashtag: string | null) => void;
  setSearchQuery: (query: string) => void;
  refreshFeed: () => Promise<void>;
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  currentUser: null,
  isAuthenticated: false,
  posts: [],
  loading: false,
  refreshing: false,
  error: null,
  hasMore: true,
  page: 1,
  activeHashtag: null,
  searchQuery: '',

  // Set current user
  setCurrentUser: (user) => set({ 
    currentUser: user,
    isAuthenticated: !!user 
  }),

  // Login với WordPress credentials
  login: async (username, password) => {
    try {
      set({ loading: true, error: null });
      const { user } = await wpApi.login(username, password);
      set({ 
        currentUser: user, 
        isAuthenticated: true,
        loading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Login failed',
        loading: false 
      });
      throw error;
    }
  },

  // Logout
  logout: () => {
    wpApi.logout();
    set({ 
      currentUser: null, 
      isAuthenticated: false 
    });
  },

  // Load feed posts
  loadFeed: async () => {
    const { activeHashtag, searchQuery } = get();
    
    try {
      set({ loading: true, error: null, page: 1 });
      
      const response = await wpApi.getFeedPosts({
        page: 1,
        per_page: 20,
        hashtag: activeHashtag || undefined,
      });
      
      // Filter by search query client-side (simple implementation)
      let posts = response.posts;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        posts = posts.filter(post => 
          post.content.toLowerCase().includes(query) ||
          post.ownerName.toLowerCase().includes(query)
        );
      }
      
      set({ 
        posts, 
        hasMore: response.has_more,
        loading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load feed',
        loading: false 
      });
    }
  },

  // Load more posts (pagination)
  loadMore: async () => {
    const { page, hasMore, posts, activeHashtag, searchQuery } = get();
    
    if (!hasMore || get().loading) return;
    
    try {
      set({ loading: true });
      
      const nextPage = page + 1;
      const response = await wpApi.getFeedPosts({
        page: nextPage,
        per_page: 20,
        hashtag: activeHashtag || undefined,
      });
      
      // Filter by search query client-side
      let newPosts = response.posts;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        newPosts = newPosts.filter(post => 
          post.content.toLowerCase().includes(query) ||
          post.ownerName.toLowerCase().includes(query)
        );
      }
      
      set({ 
        posts: [...posts, ...newPosts], 
        hasMore: response.has_more,
        page: nextPage,
        loading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load more',
        loading: false 
      });
    }
  },

  // Like a post
  likePost: async (postId) => {
    const { currentUser, posts } = get();
    if (!currentUser) return;
    
    // Optimistic update
    const updatedPosts = posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          likes: [...post.likes, currentUser.uid],
          likeCount: post.likeCount + 1,
        };
      }
      return post;
    });
    set({ posts: updatedPosts });
    
    try {
      const result = await wpApi.toggleLike(postId);
      // Đồng bộ lại số like/liked thật từ server (bỏ qua kết quả optimistic)
      set({
        posts: get().posts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              likes: result.liked
                ? (post.likes.includes(currentUser.uid)
                    ? post.likes
                    : [...post.likes, currentUser.uid])
                : post.likes.filter(id => id !== currentUser.uid),
              likeCount: result.like_count,
            };
          }
          return post;
        }),
      });
    } catch {
      // Revert on error
      set({ posts });
    }
  },

  // Unlike a post
  unlikePost: async (postId) => {
    const { currentUser, posts } = get();
    if (!currentUser) return;
    
    // Optimistic update
    const updatedPosts = posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          likes: post.likes.filter(id => id !== currentUser.uid),
          likeCount: Math.max(0, post.likeCount - 1),
        };
      }
      return post;
    });
    set({ posts: updatedPosts });
    
    try {
      const result = await wpApi.toggleLike(postId);
      // Đồng bộ lại số like/liked thật từ server (bỏ qua kết quả optimistic)
      set({
        posts: get().posts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              likes: result.liked
                ? (post.likes.includes(currentUser.uid)
                    ? post.likes
                    : [...post.likes, currentUser.uid])
                : post.likes.filter(id => id !== currentUser.uid),
              likeCount: result.like_count,
            };
          }
          return post;
        }),
      });
    } catch {
      // Revert on error
      set({ posts });
    }
  },

  // Tải lại 1 bài viết từ server và cập nhật vào feed
  refreshPost: async (postId) => {
    const fresh = await wpApi.getPost(postId);
    if (!fresh) return;
    set({
      posts: get().posts.map(post => (post.id === postId ? fresh : post)),
    });
  },

  // Delete a post (author only)
  deletePost: async (postId) => {
    try {
      await wpApi.deletePost(postId);
      set({ posts: get().posts.filter(post => post.id !== postId) });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Không thể xóa bài viết' });
      throw error;
    }
  },

  // Report a post (non-owners)
  reportPost: async (postId, reason) => {
    try {
      await wpApi.reportPost(postId, reason);
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Không thể báo cáo bài viết' });
      throw error;
    }
  },

  // Set active hashtag filter
  setActiveHashtag: (hashtag) => {
    set({ activeHashtag: hashtag, page: 1, posts: [] });
    get().loadFeed();
  },

  // Set search query
  setSearchQuery: (query) => {
    set({ searchQuery: query, page: 1 });
    get().loadFeed();
  },

  // Refresh feed
  refreshFeed: async () => {
    const { activeHashtag } = get();
    
    try {
      set({ refreshing: true, error: null });
      
      const response = await wpApi.getFeedPosts({
        page: 1,
        per_page: 20,
        hashtag: activeHashtag || undefined,
      });
      
      set({ 
        posts: response.posts, 
        hasMore: response.has_more,
        page: 1,
        refreshing: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to refresh',
        refreshing: false 
      });
    }
  },
}));
